import React, { ReactNode } from 'react'

import { SxProps } from '@mui/material'

import Box from 'components_new/atoms/Box'
import Chip, { ChipProps } from 'components_new/atoms/Chip'
import { useTranslation } from 'language/translator'
import Icon, { IconProps } from 'components_new/atoms/Icon'

import TooltipWrapper from 'components_new/atoms/TooltipWrapper'
import Divider from 'components_new/atoms/Divider'

export type ChipTabOption = {
  count?: number
  disabled?: boolean
  disabledTitle?: string
  divider?: boolean
  icon?: IconProps
  label: string | ReactNode
  value: string | null
}

export interface ChipTabsProps {
  disableAll?: boolean
  disableBackground?: boolean
  disabled?: boolean
  disablePadding?: boolean
  onChange: (value: string | null) => void
  options: ChipTabOption[]
  value: string | null
  size?: ChipProps['size']
  sx?: SxProps
}

const ChipTabs = React.forwardRef((props: ChipTabsProps, ref: any) => {
  const {
    disableAll,
    disableBackground,
    disabled,
    disablePadding,
    onChange,
    options,
    size,
    value,
    sx
  } = props

  const { t } = useTranslation()

  return (
    <Box
      ref={ref}
      sx={{
        bgcolor: disableBackground ? undefined : 'background.default',
        borderRadius: 1,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        minWidth: 0,
        p: disablePadding ? undefined : 1,
        ...sx
      }}
    >
      {!disableAll ? (
        <>
          <Chip
            clickable={true}
            color={value === null ? 'primary' : undefined}
            disabled={disabled}
            label={t('global', 'all')}
            onClick={() => onChange(null)}
            size={size}
          />

          <Divider flexItem={true} orientation="vertical" sx={{ mx: 0.5 }} />
        </>
      ) : null}
      {options.map((option) => (
        <React.Fragment key={option.value}>
          {option.divider ? (
            <Divider flexItem={true} orientation="vertical" sx={{ mx: 0.5 }} />
          ) : null}

          <TooltipWrapper
            title={option.disabled ? option.disabledTitle : undefined}
          >
            <Chip
              clickable={true}
              color={option.value === value ? 'primary' : 'transparent'}
              disabled={disabled || option.disabled}
              icon={option.icon ? <Icon {...option.icon} /> : undefined}
              label={
                option.count !== undefined ? (
                  <>
                    {option.label}
                    <Box
                      component="span"
                      sx={{ ml: 0.5, fontWeight: 400, opacity: 0.7 }}
                    >
                      {option.count}
                    </Box>
                  </>
                ) : (
                  option.label
                )
              }
              onClick={() => onChange(option.value)}
              size={size}
            />
          </TooltipWrapper>
        </React.Fragment>
      ))}
    </Box>
  )
})

ChipTabs.displayName = 'ChipTabs'
export default ChipTabs
